// ADR-0144 §4 — documentos con alcance (fase 1: alcance proyecto).
//
// Lado de la inmobiliaria (dashboard, mismo gating que el importador de
// unidades: UNIT_IMPORT_ROLE_KEYS o admin de plataforma, y receta con
// `unitProfile`):
//   GET  /asset-groups/:id/documents                    → vigentes del proyecto
//   POST /asset-groups/:id/documents/upload-url         → presign PUT (PDF)
//   POST /asset-groups/:id/documents                    → registra el objeto subido
//   POST /asset-groups/:id/documents/:documentId/retire → marca superseded_at
//   GET  /asset-groups/:id/documents/:documentId/url    → presign GET corto
//
// Lado del propietario (siempre por la unidad seleccionada, ADR-0143):
//   GET /assets/:id/documents                    → vigentes de su proyecto
//   GET /assets/:id/documents/:documentId/url    → presign GET corto
//
// Retirar nunca borra: la fila queda con `superseded_at` y el objeto en
// el bucket. Lo que el propietario ve es sólo lo vigente.
import { z } from "zod";
import { privatePresignUploadResponseSchema } from "./media.js";
/** Los tres alcances del ADR. La fase 1 sólo escribe `proyecto`; la
 *  tabla y sus CHECK ya admiten los otros dos. */
export const DOCUMENT_SCOPES = ["proyecto", "tipologia", "unidad"];
export const DOCUMENT_KINDS = [
    "manual",
    "reglamento",
    "plano",
    "acta",
    "escritura",
    "otro",
];
/** Lo que tiene sentido subir con alcance proyecto. El acta y la
 *  escritura son de UNA unidad (fase 2): subirlas al proyecto las
 *  mostraría a todos los propietarios. */
export const PROJECT_DOCUMENT_KINDS = [
    "manual",
    "reglamento",
    "plano",
    "otro",
];
export const DOCUMENT_KIND_LABEL = {
    manual: "Manual",
    reglamento: "Reglamento",
    plano: "Plano",
    acta: "Acta",
    escritura: "Escritura",
    otro: "Otro",
};
/** Sólo PDF en la fase 1 (el visor in-app es fase 3). */
export const DOCUMENT_CONTENT_TYPE = "application/pdf";
/** Igual al tope de media-svc (MAX_UPLOAD_BYTES, 10 MB): un tope mayor
 *  acá sólo movería el rechazo un paso más adentro. */
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_TITLE_MAX = 120;
export const documentUploadUrlRequestSchema = z
    .object({
    contentType: z.literal(DOCUMENT_CONTENT_TYPE),
    contentLength: z.number().int().positive().max(DOCUMENT_MAX_BYTES),
})
    .strict();
// ADR-0157 — sin `publicUrl`: el PDF nuevo vive en el bucket privado.
export const documentUploadUrlResponseSchema = privatePresignUploadResponseSchema;
export const documentCreateRequestSchema = z
    .object({
    objectKey: z.string().min(1).max(512),
    title: z.string().trim().min(1).max(DOCUMENT_TITLE_MAX),
    kind: z.enum(PROJECT_DOCUMENT_KINDS),
})
    .strict();
/** Una fila vigente, tal como la ve la inmobiliaria. */
export const projectDocumentSchema = z.object({
    id: z.string().uuid(),
    assetGroupId: z.string().uuid(),
    scope: z.enum(DOCUMENT_SCOPES),
    kind: z.enum(DOCUMENT_KINDS),
    title: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    publishedAt: z.string().datetime(),
});
export const projectDocumentsResponseSchema = z.object({
    documents: z.array(projectDocumentSchema),
});
/** Lo que ve el propietario: un documento vigente del proyecto de su
 *  unidad. Se abre pidiendo su URL presignada a
 *  GET /assets/:id/documents/:documentId/url.
 *
 *  El acta de entrega NO está acá (decisión del founder, corte 6): el
 *  PDF que hoy sale del generador no incluye el formulario firmado
 *  (`adminData.actaEntregaForm`), así que se entrega en un corte propio. */
export const ownerDocumentSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(DOCUMENT_KINDS),
    title: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    publishedAt: z.string().datetime(),
});
export const assetDocumentsResponseSchema = z.object({
    documents: z.array(ownerDocumentSchema),
});
export const documentDownloadUrlResponseSchema = z.object({
    url: z.string().url(),
    expiresAt: z.string().datetime(),
});
export const documentRetireResponseSchema = z.object({
    id: z.string().uuid(),
    supersededAt: z.string().datetime(),
});
/** Quién administra los documentos de proyecto: el mismo gating que el
 *  importador de unidades (ADR-0144 §2, UNIT_IMPORT_ROLE_KEYS — gerente
 *  de postventa y admin), más un token de admin de plataforma en la
 *  ruta. Un alias y no una lista propia: si las dos superficies se
 *  separan algún día, se separan acá, a la vista. */
export { isUnitImportRole as isProjectDocumentRole } from "./auth.js";
//# sourceMappingURL=documents.js.map